import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  List,
  ListItemButton,
  ListItemText,
  TextField,
} from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// API FUNCTIONS
import {
  apiCallAddVenueSurveySelection,
  apiCallCategoryWiseQuestion,
  apiCallQuickSurveyCategoryList,
  apiCallGetVenueQuestion,
} from "../../../../Core/Apicall";

import { useSelector } from "react-redux";
import { RootState } from "../../../../Redux/Store";

interface QuickSurveyProps {
  venueId?: string;
}

const QuickSurvey: React.FC<QuickSurveyProps> = ({ venueId }) => {
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedCat, setSelectedCat] = useState<string>("");
  const [questions, setQuestions] = useState<any[]>([]);
  const [filteredQuestions, setFilteredQuestions] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const userdata: any = useSelector((state: RootState) => state.global.token);

  // Map of selected questions per category
  const [selectedQuestionsMap, setSelectedQuestionsMap] = useState<{
    [key: string]: string[];
  }>({});
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await apiCallQuickSurveyCategoryList({});
        if (res?.status && res.data?.length > 0) {
          setCategories(res.data);
          setSelectedCat(res.data[0]._id);
        }
      } catch (err) {
      }
    };

    loadCategories();
  }, []);

useEffect(() => {
  const loadVenueSelections = async () => {
    if (!venueId) return;

    try {
      const res = await apiCallGetVenueQuestion({venueId});
      if (res?.status && res.data) {
        const cleanedMap: { [key: string]: string[] } = {};

        // Explicitly type qArr as any[]
        Object.entries(res.data.questions || {}).forEach(([catId, qArr]) => {
          const arr = qArr as any[]; // Type assertion
          cleanedMap[catId] = arr.map((q: any) =>
            typeof q === "string" ? q : q.$oid
          );
        });

        setSelectedQuestionsMap(cleanedMap);
      }
    } catch (err) {
    }
  };

  loadVenueSelections();
}, [venueId]);


  // Load questions for selected category
  useEffect(() => {
    if (!selectedCat) return;

    const loadQuestions = async (catId: string) => {
      try {
        const res = await apiCallCategoryWiseQuestion({ categoryId: catId });
        if (res?.status) {
          setQuestions(res.data);
          setFilteredQuestions(res.data);
        } else {
          setQuestions([]);
          setFilteredQuestions([]);
        }
      } catch (err) {
      }
    };

    loadQuestions(selectedCat);
  }, [selectedCat]);

  // Update currently selected questions for highlighting
  useEffect(() => {
    setSelectedQuestions(selectedQuestionsMap[selectedCat] || []);
  }, [selectedCat, selectedQuestionsMap]);

  // Select/deselect question
  const handleSelectQuestion = (id: string) => {
    setSelectedQuestionsMap((prev) => {
      const prevSelected = prev[selectedCat] || [];
      const updatedSelected = prevSelected.includes(id)
        ? prevSelected.filter((q) => q !== id)
        : [...prevSelected, id];
      return { ...prev, [selectedCat]: updatedSelected };
    });
  };

  // Save selected questions
  const handleSave = async () => {
    try {
      // Remove empty categories
      const cleanedQuestionsMap: { [key: string]: string[] } = {};
      Object.entries(selectedQuestionsMap).forEach(([catId, qIds]) => {
        if (qIds && qIds.length > 0) cleanedQuestionsMap[catId] = qIds;
      });

      const params = {
        venueId: venueId,
        questions: cleanedQuestionsMap,
        userId: userdata?.data?.userId,
      };

      await apiCallAddVenueSurveySelection(params);
      toast.success("Questions saved successfully!");
    } catch (err) {
      toast.error("Failed to save questions.");
    }
  };

  // Filter questions based on search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredQuestions(questions);
    } else {
      const normalizedSearch = searchTerm.toLowerCase().replace(/\s+/g, "");
      const filtered = questions.filter((q) =>
        q.question.toLowerCase().replace(/\s+/g, "").includes(normalizedSearch)
      );
      setFilteredQuestions(filtered);
    }
  }, [searchTerm, questions]);

  return (
    <>
      <ToastContainer />
      <Paper sx={{ p: 3, width: "100%", borderRadius: 3 }}>
        <Box display="flex" justifyContent="end" alignItems="center" mb={2}>
          <Box display="flex" gap={1} alignItems="center">
            <TextField
              placeholder="Search questions..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Button
              variant="contained"
              color="primary"
              sx={{ background: "#000", "&:hover": { background: "#333" } }}
              onClick={handleSave}
            >
              Save
            </Button>
          </Box>
        </Box>

        <Box display="flex" gap={3} height="70vh">
          {/* Categories */}
          <Box
            minWidth={220}
            borderRight="1px solid #ddd"
            pr={2}
            sx={{
              maxHeight: "100%",
              overflowY: "auto",
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#ccc",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-track": { backgroundColor: "#f0f0f0" },
              scrollbarWidth: "thin",
              scrollbarColor: "#ccc #f0f0f0",
            }}
          >
            <List>
              {categories.map((cat) => {
                const count = selectedQuestionsMap[cat._id]?.length || 0;
                return (
                  <ListItemButton
                    key={cat._id}
                    selected={selectedCat === cat._id}
                    onClick={() => setSelectedCat(cat._id)}
                    sx={{
                      borderRadius: 1,
                      "&:hover": {
                        background:
                          selectedCat === cat._id ? "#333" : "#f0f0f0",
                      },
                      "&.Mui-selected": {
                        background: "#000",
                        color: "#fff",
                        fontWeight: 700,
                      },
                      "&.Mui-selected:hover": { background: "#333" },
                    }}
                  >
                    <ListItemText
                      primary={`${cat.category} ${
                        count > 0 ? `(${count})` : ""
                      }`}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>

          {/* Questions */}
          <Box
            flex={1}
            sx={{
              maxHeight: "100%",
              overflowY: "auto",
              px: 1.5,
              py: 1,
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#ccc",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-track": { backgroundColor: "#f0f0f0" },
              scrollbarWidth: "thin",
              scrollbarColor: "#ccc #f0f0f0",
            }}
          >
            {filteredQuestions.length === 0 && (
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                No questions found.
              </Typography>
            )}

            {filteredQuestions.map((q) => (
              <Paper
                key={q._id}
                onClick={() => handleSelectQuestion(q._id)}
                sx={{
                  p: 1.5,
                  mb: 1.5,
                  cursor: "pointer",
                  border: selectedQuestions.includes(q._id)
                    ? "1.5px solid #000"
                    : "1.5px solid #ddd",
                  background: selectedQuestions.includes(q._id)
                    ? "#000"
                    : "white",
                  borderRadius: 2,
                  transition: "0.2s",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: selectedQuestions.includes(q._id) ? "#FFF" : "#333",
                  }}
                >
                  {q.question}
                </Typography>

                <Typography
                  sx={{
                    fontSize: "13px",
                    color: selectedQuestions.includes(q._id) ? "#FFF" : "#333",
                  }}
                >
                  Type:{" "}
                  {q.type === "radio"
                    ? "Radio"
                    : q.type === "text"
                    ? "Text"
                    : "TextInput"}
                </Typography>

                {q.type === "radio" && q.options?.length > 0 && (
                  <Box mt={1} sx={{ display: "flex", gap: 2 }}>
                    {q.options.map((opt: string, idx: number) => (
                      <Box
                        key={idx}
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          border: "1px solid #ccc",
                          borderRadius: 1,
                          fontSize: "13px",
                          background: "#fafafa",
                        }}
                      >
                        ● {opt}
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            ))}
          </Box>
        </Box>
      </Paper>
    </>
  );
};

export default QuickSurvey;
